import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

import { messages } from '../../i18n/messages';

import styles from './Slider.module.scss';

const propTypes = {

	/**
	 * Custom class names to add to the top-level "slider" element.
	 */
	className: PropTypes.string,

	/**
	 * The list of items to display.
	 */
	items: PropTypes.arrayOf(PropTypes.element),

	/**
	 * The number of items to show per slider row.
	 */
	numPerRow: PropTypes.oneOf([1, 2, 3, 4]),

	/**
	 * The number of rows to display per slider page.
	 */
	numRows: PropTypes.number,

	/**
	 * The index of the item to display as the initial page.
	 */
	startIndex: PropTypes.number,

	/**
	 * The font size of the arrow icon.
	 */
	arrowSize: PropTypes.string,

	/**
	 * The left and right margin between slider items.
	 */
	margin: PropTypes.string,

};

const defaultProps = {
	className: '',
	items: [],
	numPerRow: 3,
	numRows: 1,
	startIndex: 0,
	arrowSize: '15px',
	margin: '1rem',
};

/**
 * Shared component to render a basic TechChange-style slider list of items.
 *
 * @param {Object} props The props.
 *
 * @returns {Object} The rendered component.
 */
const Slider = (props) => {

	const {
		className,
		items,
		numPerRow,
		numRows,
		startIndex,
		arrowSize,
		margin,
		...restProps
	} = props;

	const intl = useIntl();

	// Ensure the provided start index is within range
	const initialIndex = useMemo(() => Math.min(Math.max(startIndex, 0), items.length - 1),
		[startIndex, items]);

	const numPerPage = numPerRow * numRows;

	// Set initial page index
	const [currentPage, setCurrentPage] = useState(Math.floor(initialIndex / numPerPage));

	useEffect(() => {

		setCurrentPage(Math.floor(initialIndex / numPerPage));

	}, [initialIndex, numPerPage]);

	// Calculate page index ranges
	const totalPages = Math.ceil(items.length / numPerPage);
	const PAGE_IDX_MIN = 0;
	const PAGE_IDX_MAX = totalPages - 1;

	const handleNext = () => {
		setCurrentPage((prev) => Math.min(prev + 1, PAGE_IDX_MAX));
	};

	const handlePrevious = () => {
		setCurrentPage((prev) => Math.max(prev - 1, PAGE_IDX_MIN));
	};

	// Checks if the item at index should be hidden based on the number of items to show
	const hideItem = (index) =>
		index < (currentPage * numPerPage) ||
		index >= ((currentPage + 1) * numPerPage);

	const showLeftArrowButton = currentPage > PAGE_IDX_MIN;
	const showRightArrowButton = currentPage < PAGE_IDX_MAX;

	const arrowStyles = {
		fontSize: arrowSize,
	};

	return (
		<div
			className={className}
			aria-label={intl.formatMessage(messages.TC_COMPONENTS_SLIDER_DEFAULT_ARIA_LABEL)}
			role="region"
			{...restProps}
			className={styles.slider}>
			{showLeftArrowButton &&
				<button
					className={`btn arrow back-arrow ${styles.arrow}`}
					style={Object.assign({}, arrowStyles, {
						left: `calc(-${margin} / 2)`
					})}
					aria-label={intl.formatMessage(messages.TC_COMPONENTS_SLIDER_PREVIOUS_BUTTON_ARIA_LABEL, {
						numPerPage,
					})}
					onClick={handlePrevious}>
					<i className="fas fa-chevron-left"></i>
				</button>
			}
			<div
				className={styles['slider-page']}
				role="group"
				aria-label={intl.formatMessage(messages.TC_COMPONENTS_SLIDER_PAGE_ARIA_LABEL, {
					index: currentPage + 1,
					total: totalPages
				})}>
				{items.map((item, idx) => (
					<div
						key={item.id || idx}
						className={`slider-item ${styles['slider-item']} ${hideItem(idx) ? 'd-none' : ''}`}
						style={{
							width: `calc((100%  / ${numPerRow}) - (2 * ${margin}))`,
							minWidth: `calc((100%  / ${numPerRow}) - (2 * ${margin}))`,
							maxWidth: `calc((100%  / ${numPerRow}) - (2 * ${margin}))`,
							margin: `0 ${margin}`,
						}}>
						{item}
					</div>
				))}
			</div>
			{showRightArrowButton &&
				<button
					className={`btn arrow next-arrow ${styles.arrow}`}
					style={Object.assign({}, arrowStyles, {
						right: `calc(-${margin} / 2)`
					})}
					aria-label={intl.formatMessage(messages.TC_COMPONENTS_SLIDER_NEXT_BUTTON_ARIA_LABEL, {
						numPerPage,
					})}
					onClick={handleNext}>
					<i className="fas fa-chevron-right"></i>
				</button>
			}
		</div>
	);

};

Slider.defaultProps = defaultProps;
Slider.propTypes = propTypes;

export default Slider;
